import { useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Link } from 'react-router-dom';
import RouteConstant from '../../constants/route.constants';
import { useCustomerDelivery } from '../../providers/CustomerDeliveryProvider';
import { currentLocationIssueMessage } from '../../services/products/current-location.service';
import './delivery-location.css';

export default function DeliveryLocation() {
  const { userId, data, loading, error, isHomePage, refresh, selectAddress, selectPincode,
    locating, currentLocation, locationFallbackId, locationIssue, selectCurrentLocation } = useCustomerDelivery();
  const [visible, setVisible] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [pincode, setPincode] = useState('');
  const promptedAttempt = useRef<number | null>(null);
  const needsPincode = data?.requiresPincode === true;
  useEffect(() => { setVisible(false); setSelectionError(null); setPincode(''); }, [userId]);
  useEffect(() => {
    // A missing pincode alone must not interrupt initial location detection.
    // Prompt once per failed attempt, including when a saved address is available.
    if (isHomePage && !loading && !locating && !currentLocation && locationFallbackId != null && promptedAttempt.current !== locationFallbackId) {
      promptedAttempt.current = locationFallbackId;
      setVisible(true);
    }
  }, [isHomePage, loading, locating, currentLocation, locationFallbackId]);
  if (!isHomePage) return null;
  const selected = data?.addresses.find(a => a.userAddressId === data.selectedAddressId);
  const label = locating ? 'Finding your location…' : currentLocation ?
    [currentLocation.cityName, currentLocation.areaName, currentLocation.pincode].filter(Boolean).join(', ') :
    selected ? [selected.cityName, selected.areaName].filter(Boolean).join(', ') || 'Saved address' :
    data?.pincode ? data.pincode : loading ? 'Checking delivery…' : 'Enter delivery pincode';
  const mobileLabel = locating ? label : currentLocation?.cityName || (currentLocation ? currentLocation.pincode : selected?.cityName) || label;
  const options = (data?.addresses ?? []).map(a => ({
    value: a.userAddressId, label: [a.address1, a.address2, a.areaName, a.cityName].filter(Boolean).join(', ') || 'Saved address'
  }));
  const changeAddress = async (id: number) => {
    setSelectionError(null);
    try { await selectAddress(id); setVisible(false); }
    catch (failure) { setSelectionError(failure instanceof Error ? failure.message : 'Could not change address.'); }
  };
  return <div className="customer-delivery-location">
    <button type="button" className="delivery-location-button" aria-label={(currentLocation ? 'Current location: ' : 'Delivery address: ') + label}
      onClick={() => { setSelectionError(null); setVisible(true); }} aria-haspopup="dialog">
      <i className={loading || locating ? 'pi pi-spin pi-spinner' : 'pi pi-map-marker'} aria-hidden="true" />
      <span>
        <small>{currentLocation ? 'Current location' : 'Deliver to'}</small>
        <strong className="delivery-label-full" title={label}>{label}</strong>
        <strong className="delivery-label-mobile" title={label}>{mobileLabel}</strong>
        {currentLocation && <span className="delivery-maps-attribution" translate="no">Google Maps</span>}
      </span>
      <i className="pi pi-angle-down" aria-hidden="true" />
    </button>
    <Dialog header="Delivery location" className="delivery-location-dialog" visible={visible} onHide={() => setVisible(false)}
      style={{ width: 'min(94vw, 480px)' }}>
      {options.length > 0 ? <p>Select one of your saved addresses. This becomes your default delivery address.</p> :
        <p>Enter your pincode to check product availability. You can add your delivery address later.</p>}
      {currentLocation && <p>Product availability uses your current location. Confirm your delivery address at checkout.</p>}
      <Button label={locating ? 'Finding your location…' : 'Use current location'} icon="pi pi-map-marker"
        className="mb-3" loading={locating} disabled={loading || locating} onClick={async () => {
          setSelectionError(null);
          try { await selectCurrentLocation(); setVisible(false); }
          catch (failure) { setSelectionError(failure instanceof Error ? failure.message : 'Could not use your current location.'); }
        }} />
      {options.length > 0 && <div className="delivery-address-field">
        <label htmlFor="home-delivery-address" className="delivery-address-label">Choose a saved address</label>
        <Dropdown inputId="home-delivery-address" aria-label="Delivery address"
        value={currentLocation || locating ? null : data?.selectedAddressId} placeholder="Choose a saved address" options={options} className="delivery-address-select w-full" disabled={loading}
        onChange={event => { if (typeof event.value === 'number') void changeAddress(event.value); }} />
      </div>}
      {!loading && data && options.length === 0 && <p>No saved addresses. <Link to={RouteConstant.userAddresses}>Add a delivery address</Link>.</p>}
      {options.length === 0 && <div className="flex gap-2 mt-3">
        <InputText aria-label="Delivery pincode" inputMode="numeric" maxLength={6} placeholder="Six-digit pincode"
          value={pincode} disabled={loading} onChange={event => setPincode(event.target.value.replace(/\D/g, '').slice(0, 6))} />
        <Button label="Check availability" loading={loading} disabled={!/^[1-9][0-9]{5}$/.test(pincode)} onClick={async () => {
          setSelectionError(null);
          try { await selectPincode(pincode); setVisible(false); }
          catch (failure) { setSelectionError(failure instanceof Error ? failure.message : 'Could not check this pincode.'); }
        }} />
      </div>}
      {options.length > 0 && needsPincode && !locating && <p role="alert">The selected address needs a valid six-digit pincode. <Link to={RouteConstant.userAddresses}>Update your address</Link>.</p>}
      {data?.pincode && <p>Checking availability for pincode <strong>{data.pincode}</strong>.</p>}
      {(selectionError || error) && <p role="alert" className="p-error">{selectionError || error}</p>}
      {!selectionError && !error && locationFallbackId != null && <p role="status" className="delivery-location-notice">{currentLocationIssueMessage(locationIssue)}</p>}
      {error && <Button label="Retry availability" icon="pi pi-refresh" loading={loading}
        onClick={() => { setSelectionError(null); void refresh(); }} />}
    </Dialog>
  </div>;
}
